import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import Navigation from './components/Navigation'
import Hero from './components/Hero'
import Projects from './components/Projects'
import Fun from './components/Fun'
import Contact from './components/Contact'
import Background3D from './components/Background3D'
import CustomCursor from './components/CustomCursor'
import FishDivider from './components/BearDivider'

const ENABLE_CUSTOM_CURSOR = false; // Set to true to enable, false to disable

function App() {
  const [currentSection, setCurrentSection] = useState('home')
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Simulate loading time for smooth initial animation
    const timer = setTimeout(() => setIsLoading(false), 500)
    return () => clearTimeout(timer)
  }, [])

  // Add/remove hide-cursor class on <body> based on ENABLE_CUSTOM_CURSOR
  useEffect(() => {
    if (ENABLE_CUSTOM_CURSOR) {
      document.body.classList.add('hide-cursor')
    } else {
      document.body.classList.remove('hide-cursor')
    }
  }, [])

  // Mobile scrolling optimizations
  useEffect(() => {
    // Prevent zoom on double tap for mobile
    let lastTouchEnd = 0;
    const preventZoom = (event: TouchEvent) => {
      const now = (new Date()).getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Prevent pull-to-refresh on mobile
    const preventPullToRefresh = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      
      const touch = event.touches[0];
      const startY = touch.clientY;
      
      const handleTouchMove = (moveEvent: TouchEvent) => {
        const moveY = moveEvent.touches[0].clientY;
        const diffY = moveY - startY;
        
        if (diffY > 0 && window.scrollY === 0) {
          moveEvent.preventDefault();
        }
      };
      
      const handleTouchEnd = () => {
        document.removeEventListener('touchmove', handleTouchMove);
        document.removeEventListener('touchend', handleTouchEnd);
      };
      
      document.addEventListener('touchmove', handleTouchMove, { passive: false });
      document.addEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchend', preventZoom, false);
    document.addEventListener('touchstart', preventPullToRefresh, { passive: false });

    return () => {
      document.removeEventListener('touchend', preventZoom);
      document.removeEventListener('touchstart', preventPullToRefresh);
    };
  }, []);

  const handleSectionChange = (section: string) => {
    setCurrentSection(section)
    const element = document.getElementById(section)
    if (element) {
      // Use smooth scrolling only on desktop
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      element.scrollIntoView({ 
        behavior: isMobile ? 'auto' : 'smooth' 
      })
    }
  }

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-white">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="flex space-x-1"
        >
          <motion.div
            className="w-3 h-3 bg-primary rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="w-3 h-3 bg-primary rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
          />
          <motion.div
            className="w-3 h-3 bg-primary rounded-full"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
          />
        </motion.div>
      </div>
    )
  }

  return (
    <div className="relative min-h-screen">
      {/* Custom Cursor */}
      {ENABLE_CUSTOM_CURSOR && <CustomCursor />}
      
      {/* 3D Background */}
      <Background3D />
      
      {/* Fish Divider */}
      <FishDivider />
      
      {/* Navigation */}
      <Navigation currentSection={currentSection} onSectionChange={handleSectionChange} />
      
      {/* Main Content */}
      <main className="relative z-10">
        <AnimatePresence mode="wait">
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
          >
            {/* Hero Section */}
            <section id="home" className="min-h-screen flex items-center justify-center section-padding">
              <Hero />
            </section>

            {/* Projects Section */}
            <section id="projects" className="min-h-screen section-padding">
              <Projects />
            </section>

            {/* Fun Section */}
            <section id="fun" className="min-h-screen section-padding">
              <Fun />
            </section>

            {/* Contact Section */}
            <section id="contact" className="section-padding">
              <Contact />
            </section>
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  )
}

export default App 